import { Navigate, useLocation } from "react-router-dom";

import {
  useAppProperties,
  useCollection,
  useRedirectGatedContent,
  useVideo,
  useVideoFiles,
} from "@/hooks";
import { useResponsiveness, useUser } from "@/providers/context";
import {
  ContentThumbnail,
  Description,
  ErrorPage,
  PageLoader,
  ResourcesButton,
  Skeleton,
} from "@/components";
import { Header } from "@/app/modules/Navigation";

import { cn, getContentThumbnailUrl, getLanguageCode } from "@/utils";
import { ITEM_TYPE_VIDEO, QUERY_COURSE_ID, QUERY_PARENT_ID, VIDEO_TYPE_AUDIO } from "@/const.ts";
import { SaveToPlaylistButton } from "@/app/modules/Playlists";
import { CommentButton, Comments } from "@/app/modules/Comments";
import { ScheduleButton } from "@/app/modules/Calendar";

import { HLSVideoPlayer } from "./components/HLSVideoPlayer.tsx";
import { UpNext } from "./components/UpNext.tsx";
import { LiveStreamEnded } from "./components/LiveStreamEnded.tsx";
import { LiveStreamCountdown } from "./components/LiveStreamCountdown.tsx";
import { CompleteButton } from "./components/CompleteButton.tsx";
import { useLivestream } from "./hooks/useLivestream.tsx";
import { UpSell } from "./components/UpSell.tsx";
import { useActionsLoading } from "@/app/modules/ContentDetails/hooks/useActionsLoading.tsx";
import { CommentsProps } from "../Comments/Comments.tsx";

interface VideoDetailsProps {
  id: number;
}

export const VideoDetails = ({ id }: VideoDetailsProps) => {
  const { search, state } = useLocation();
  const query = new URLSearchParams(search);

  const { data: video, isLoading, isError } = useVideo(id);
  const { data: files } = useVideoFiles({ video, videoId: id });
  const { isVideoUnlocked, uid, products } = useUser();
  const isUnlocked = isVideoUnlocked(video);
  const { getRedirectPath } = useRedirectGatedContent();
  const { drawerMargin, transitionStyle } = useResponsiveness();
  const { loading: actionsLoading } = useActionsLoading({
    itemId: video?.VideoId,
    dataSource: video?.DataSource,
    sourceId: video?.SourceId,
    itemType: ITEM_TYPE_VIDEO,
    liveEventStartDate: video?.Properties?.LiveEventStartDate,
    liveEventEnded: video?.Properties?.LiveStreamEnded,
  });
  const { data: properties } = useAppProperties();

  const parentId = query.get(QUERY_PARENT_ID);
  const courseId = query.get(QUERY_COURSE_ID);
  const { data: parentCollection, isLoading: isParentCollectionLoading } =
    useCollection(parentId ? parseInt(parentId) : 0);

  // Upcoming means its not playable yet and shows the countdown
  // Ended means we just show the overlay
  // Otherwise we attempt to play it like any other video
  const { isLiveEnded, liveStartDate, isUpcomingLive } = useLivestream({
    video,
    videoId: id,
  });

  const hlsUrl = (files ?? []).find((f) => f.Type === "HLS")?.URL;
  const subtitles = (files ?? [])
    .filter((f) => f.LanguageDisplayName)
    .map((f) => {
      if (f.LanguageCode) {
        return f;
      }

      return {
        ...f,
        LanguageCode: getLanguageCode(f.LanguageDisplayName), // Try to find the language code from the display name
      };
    });

  if (isLoading) {
    return <PageLoader />;
  }

  if (!video || isError) {
    return <ErrorPage code={404} />;
  }
  if (video && !isUnlocked) {
    if (uid && products && products.length > 0) {
      return (
        <div>
          <Header className="px-7" />
          <UpSell />
        </div>
      );
    }
    return (
      <Navigate
        to={getRedirectPath({
          item: { Type: ITEM_TYPE_VIDEO, ChildId: id.toString() },
        })}
        replace
      />
    );
  }
  if (video.Type === VIDEO_TYPE_AUDIO) {
    return (
      <Navigate
        state={state}
        to={`/audio/${id}${!!query && "?" + query.toString()}`}
        replace
      />
    );
  }
  if (video && parentId && !parentCollection && !isParentCollectionLoading) {
    return <Navigate to={`/video/${id}`} replace />;
  }
  const hideThumbnail = video.Type === "post" && !video?.ThumbnailSource;

  const commentsBelowPlayer =
    properties?.WebItemDetailsDesign === "CommentsBelowPlayer" || !!courseId;
  const marginRight = commentsBelowPlayer ? "0" : drawerMargin;

  const commentsProps: CommentsProps = {
    dataSource: video.DataSource,
    itemType: ITEM_TYPE_VIDEO,
    sourceId: video.SourceId,
    itemId: video.VideoId,
    liveEventStartDate: video.Properties?.LiveEventStartDate,
    liveEventEnded: video.Properties?.LiveStreamEnded,
  };

  return (
    <div style={{ marginRight, transition: transitionStyle }}>
      <Header className="px-[28px]" />
      <div className={commentsBelowPlayer ? "flex justify-center" : ""}>
        <div
          className={cn(
            "flex h-full w-full flex-col px-[28px] pb-40 text-item-primary",
            commentsBelowPlayer ? "max-w-[1200px]" : "mx-auto max-w-[970px]",
          )}
        >
          <div className="w-full">
            {!hideThumbnail && (
              <div className="aspect-video overflow-hidden rounded-[12px]">
                {isLiveEnded ? (
                  <LiveStreamEnded />
                ) : isUpcomingLive && liveStartDate ? (
                  <LiveStreamCountdown startDate={liveStartDate} />
                ) : !hlsUrl ? (
                  <ContentThumbnail
                    thumbnail={getContentThumbnailUrl(
                      video,
                      "large",
                      "landscape",
                    )}
                    className="w-full"
                  />
                ) : (
                  <HLSVideoPlayer
                    key={video.VideoId}
                    src={hlsUrl!}
                    video={video}
                    autoPlay={!!liveStartDate || state?.autoPlay}
                    subtitles={subtitles}
                  />
                )}
              </div>
            )}
            <div className="h4-bold mt-[28px]">{video.Title}</div>
            {video.Description && (
              <Description
                desc={video.Description}
                className="pt-[12px]"
                autoExpand={video.Type === "post"}
              />
            )}
            {!!uid && (
              <div className="mt-7 flex gap-3">
                {actionsLoading ? (
                  <>
                    <Skeleton className="h-8 w-[72px]" />
                    <Skeleton className="h-8 w-[104px]" />
                    <Skeleton className="h-8 w-[101px]" />
                  </>
                ) : (
                  <>
                    <SaveToPlaylistButton
                      ItemId={video.VideoId}
                      ItemType={ITEM_TYPE_VIDEO}
                      SourceItemId={video.SourceId}
                    />
                    <CompleteButton video={video} />
                    <ScheduleButton video={video} />
                    {!commentsBelowPlayer && (
                      <CommentButton {...commentsProps} />
                    )}
                    <ResourcesButton resources={video.Resources} />
                  </>
                )}
              </div>
            )}
            {commentsBelowPlayer ? (
              <>
                <Comments showHeader {...commentsProps} />
              </>
            ) : (
              <UpNext currentItemId={id} />
            )}
          </div>
        </div>
        {commentsBelowPlayer && <UpNext currentItemId={id} isSidePanel />}
      </div>
    </div>
  );
};
